import orderBy from "lodash/orderBy";
import isEmpty from "lodash/isEmpty";
import moment from "../../../moment";
import { isPlanned, isUserPlannedForTraining } from "./planningHelpers";

export { generateColumns, TABLE_QUALIFICATIONS } from "./qualificationColumns";

const dateInPast = (firstDate, secondDate) => {
  return firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0);
};

const transformerForExpiryDate = (expiryDate, trans, format) => {
  if (expiryDate) {
    return format({ date: expiryDate });
  }
  return trans("trg.validity_field.no_expiry");
};

const isFromPreviousFunctions = (qualification, row) => {
  let previousUserQualification = row.previous_user_qualifications;
  if (!isEmpty(previousUserQualification)) {
    return previousUserQualification.includes(qualification.id);
  }
  return false;
};

const applyQualificationFilters = (qualifications, missingTrainings, filters) => {
  const qualificationStatus = filters["filters[qualificationStatus]"];

  // For status filters that map directly to backend-filtered data, do not merge
  // missingTrainings: the completions list used to compute them is incomplete
  // (only filtered qualifications are returned), so valid qualifications would be
  // incorrectly treated as "not done" and appear as Pending.
  const backendFilteredStatuses = [
    "expired",
    "valid",
    "expiring_soon",
    "planned",
    "expired_expiring",
    "from_previous_function"
  ];
  if (backendFilteredStatuses.includes(qualificationStatus)) {
    let result = qualifications;
    if (Object.hasOwn(filters, "filters[trainingId]")) {
      result = result.filter((item) => item.training_id === filters["filters[trainingId]"]);
    }
    return result;
  }

  let result = [...qualifications, ...missingTrainings].filter(
    (item, index, arr) => index === arr.findIndex((i) => i.training_id === item.training_id)
  );

  if (qualificationStatus === "pending") {
    result = result.filter((item) => item.isPending);
  }

  if (qualificationStatus === "all_without_previous_function") {
    result = result.filter((item) => !item.from_previous_function);
  }

  if (Object.hasOwn(filters, "filters[trainingId]")) {
    result = result.filter((item) => item.training_id === filters["filters[trainingId]"]);
  }

  return result;
};

const formatDateForReportingAndQualification = (row, format) => {
  const result = {};
  if (row.user_detail.contract_started) {
    result.contractStartedFormatted = format({ date: row.user_detail.contract_started });
  }

  if (row.user_detail.contract_ended) {
    result.contractEndedFormatted = format({ date: row.user_detail.contract_ended });
  }

  return result;
};

const mapUserFunctions = (functionsArg, completionsArg, trans) => {
  const functions = functionsArg || [];
  const completions = completionsArg || [];
  return functions
    .filter((func) => !!func.staff_functions?.training_programs)
    .flatMap((func) => {
      func.missingTrainingsPerFunction = func.staff_functions.training_programs.active_job_title_trainings
        .sort((a, b) => b.required - a.required)
        .map((item) => {
          const trainingId = item.training_id;

          const isDone = completions.some((c) => c.trainingId === trainingId);
          if (isDone) {
            return null;
          }

          if (item.training.linked_training_id) {
            const linkedTrainingId = item.training.linked_training_id;

            const linkedTrainingCompletions = completions
              .filter((c) => c.trainingId === linkedTrainingId)
              .sort((a, b) => new Date(b.validity_start) - new Date(a.validity_start));

            if (linkedTrainingCompletions.length) {
              const availableFrom = moment(linkedTrainingCompletions[0].validity_start).add(
                item.training.linked_training_notification,
                "days"
              );

              const isAvailable = moment().diff(availableFrom, "milliseconds") >= 0;
              return isAvailable && item.required ? item : null;
            }

            return null;
          }

          return item.required ? item : null;
        })
        .filter((item) => item !== null)
        .map((item) => {
          item.expiry_date = trans("trg.qualifications.training_not_done");
          item.isPending = true;
          item.notDoneYet = true;
          return item;
        });

      return func.missingTrainingsPerFunction;
    });
};

export const prepareQualificationsData = (rows, trans, format, filters) => {
  let result = rows
    .map((row) => {
      row.missingTrainings = [];
      row.functions = [];
      row.qualifications = row.qualifications.map((qualification) => {
        qualification.from_previous_function = isFromPreviousFunctions(qualification, row);
        const expiryDate = qualification.expiry_date;

        if (expiryDate) {
          qualification.date_in_past = dateInPast(new Date(expiryDate), new Date());
        }

        qualification.expiry_date = transformerForExpiryDate(qualification.expiry_date, trans, format);
        qualification.isPlanned = isPlanned(expiryDate, qualification, row);

        return qualification;
      });

      if (row.user_detail) {
        row = { ...row, ...formatDateForReportingAndQualification(row, format) };
      }

      if (row.active_staff_job_functions) {
        row.functions = row.active_staff_job_functions.map((item) => {
          item.name = item.staff_functions.name;
          return item;
        });

        const completions = row.qualifications.map((item) => ({
          trainingId: item.training.id,
          date: item.first_completed_date,
          validity_start: item.last_qualification_history?.validity_start_date || item.first_completed_date
        }));

        if (row.functions?.length) {
          const areaIdFilter = filters["filters[areaId]"];
          const functionsForPending = areaIdFilter
            ? row.functions.map((fn) => ({
                ...fn,
                staff_functions: fn.staff_functions
                  ? {
                      ...fn.staff_functions,
                      training_programs: fn.staff_functions.training_programs
                        ? {
                            ...fn.staff_functions.training_programs,
                            active_job_title_trainings:
                              fn.staff_functions.training_programs.active_job_title_trainings?.filter(
                                (item) =>
                                  Number.parseInt(item.training?.area_id, 10) === Number.parseInt(areaIdFilter, 10)
                              ) || []
                          }
                        : fn.staff_functions.training_programs
                    }
                  : fn.staff_functions
              }))
            : row.functions;
          row.missingTrainings = mapUserFunctions(functionsForPending, completions, trans);

          /**
           * Order missing trainings by training name (alphabetically)
           */
          if (row.missingTrainings.length > 0) {
            row.missingTrainings.map((item) => (item.isPlanned = isUserPlannedForTraining(row, item.training)));
            row.missingTrainings = [...orderBy(row.missingTrainings, ["training.name"], ["asc"])];
          }
        }
      }

      if (Object.hasOwn(filters, "filters[qualificationStatus]")) {
        row.qualifications = applyQualificationFilters(row.qualifications, row.missingTrainings, filters);
      } else {
        row.qualifications = [...row.qualifications, ...row.missingTrainings].filter(
          (item, index, arr) => index === arr.findIndex((i) => i.training_id === item.training_id)
        );
      }

      if (row.missingTrainings.length === 0 && row.qualifications.length === 0) {
        return null;
      }

      /**
       * Change order for the qualification from previous function
       */
      row.qualificationsPreviousFunction = row.qualifications.filter((item) => {
        return item.from_previous_function;
      });

      row.qualifications = row.qualifications.filter((item) => {
        return !item.from_previous_function;
      });
      row.qualifications = [...row.qualifications, ...row.qualificationsPreviousFunction];

      return row;
    })
    .filter((item) => item !== null);

  result = result.reduce((acc, item) => {
    item.qualifications.forEach((qualificationItem, idx) => {
      acc.push({
        ...item,
        key: `${item.id}-${qualificationItem.id}`,
        id: `${item.id}-${qualificationItem.id}`,
        qualification: { ...qualificationItem, ...(idx === 0 ? { rowSpan: item.qualifications?.length || 1 } : {}) }
      });
    });
    return acc;
  }, []);

  return result;
};
